import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const todayLabel = today.toISOString().slice(0, 10);

const emptyForm = {
  customerUserId: '',
  skuId: '',
  serviceName: '',
  skuDescription: '',
  purchasedQuantity: '',
  purchasedUnit: '',
  quotedUnitRate: '',
  notes: '',
};

const fmt = (value, digits = 2) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });

const fmtOptional = (value, digits = 4) =>
  value === null || value === undefined || value === '' ? '-' : fmt(value, digits);

const unitOptions = ['Hours', 'GiB', 'GiB Month', 'GB', 'GB Month', 'Count', 'Month'];

export default function GoogleUsageReportPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(todayLabel);
  const [form, setForm] = useState(emptyForm);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await api.get('/users');
    const customers = (data.users || []).filter((user) =>
      user.is_active !== false && ['client', 'client_user'].includes(user.role)
    );
    setUsers(customers);
    setSelectedCustomerId((current) => current || String(customers[0]?.id || ''));
    setForm((current) => ({ ...current, customerUserId: current.customerUserId || String(customers[0]?.id || '') }));
  }, [isAdmin]);

  const loadMappings = useCallback(async (customerId = selectedCustomerId) => {
    const params = new URLSearchParams();
    if (isAdmin && customerId) params.set('customerUserId', customerId);
    const { data } = await api.get(`/google-usage/mappings${params.toString() ? `?${params.toString()}` : ''}`);
    setMappings(data.mappings || []);
  }, [isAdmin, selectedCustomerId]);

  const loadReport = useCallback(async () => {
    if (isAdmin && !selectedCustomerId) return;

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (isAdmin) params.set('customerUserId', selectedCustomerId);
      const { data } = await api.get(`/google-usage/report?${params.toString()}`);
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load Google usage report');
    } finally {
      setLoading(false);
    }
  }, [endDate, isAdmin, selectedCustomerId, startDate]);

  useEffect(() => {
    loadUsers().catch(() => setError('Failed to load customers'));
  }, [loadUsers]);

  useEffect(() => {
    loadMappings().catch(() => setError('Failed to load SKU mappings'));
  }, [loadMappings]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const rows = report?.rows || [];
  const summary = report?.summary || {};
  const groupedRows = useMemo(() => rows.reduce((acc, row) => {
    const service = row.serviceName || 'Google Cloud';
    if (!acc[service]) acc[service] = [];
    acc[service].push(row);
    return acc;
  }, {}), [rows]);
  const selectedCustomer = useMemo(
    () => users.find((user) => String(user.id) === String(selectedCustomerId)),
    [selectedCustomerId, users]
  );

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setForm((current) => ({ ...current, customerUserId: customerId }));
  };

  const saveMapping = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      await api.post('/google-usage/mappings', {
        ...form,
        customerUserId: form.customerUserId || selectedCustomerId,
      });
      setMessage('SKU mapping saved');
      setForm({ ...emptyForm, customerUserId: selectedCustomerId });
      await loadMappings(selectedCustomerId);
      await loadReport();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SKU mapping');
    } finally {
      setSaving(false);
    }
  };

  const removeMapping = async (mappingId) => {
    setError('');
    setMessage('');
    try {
      await api.delete(`/google-usage/mappings/${mappingId}`);
      setMessage('SKU mapping removed');
      await loadMappings(selectedCustomerId);
      await loadReport();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove SKU mapping');
    }
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Google Usage Report </h1> <h3 style={s.subtitle}>Please note Google Cloud usage data may take up to 24 hours to update</h3>
            <p style={s.subtitle}>Purchased SKU usage, storage capacity, utilized hours, and remaining balance</p>
          </div>
          <button style={s.primaryBtn} onClick={loadReport} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div style={s.filters}>
          {isAdmin && (
            <div style={s.fieldWide}>
              <label style={s.label}>Customer</label>
              <select value={selectedCustomerId} onChange={(e) => handleCustomerChange(e.target.value)} style={s.input}>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                ))}
              </select>
            </div>
          )}
          <div style={s.field}>
            <label style={s.label}>Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={s.input} />
          </div>
          <div style={s.field}>
            <label style={s.label}>End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={s.input} />
          </div>
        </div>

        {isAdmin && (
          <section style={s.section}>
            <div style={s.sectionHead}>
              <h2 style={s.sectionTitle}>SKU Configuration</h2>
              <span style={s.badge}>{mappings.length} mapped</span>
            </div>
            <form onSubmit={saveMapping} style={s.mappingForm}>
              <select
                value={form.customerUserId || selectedCustomerId}
                onChange={(e) => setForm({ ...form, customerUserId: e.target.value })}
                style={s.input}
                required
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
              <input placeholder="SKU ID" value={form.skuId} onChange={(e) => setForm({ ...form, skuId: e.target.value })} style={s.input} required />
              <input placeholder="Service name (optional)" value={form.serviceName} onChange={(e) => setForm({ ...form, serviceName: e.target.value })} style={s.input} />
              <input placeholder="SKU description (optional)" value={form.skuDescription} onChange={(e) => setForm({ ...form, skuDescription: e.target.value })} style={s.input} />
              <input type="number" step="0.0001" min="0" placeholder="Purchased qty" value={form.purchasedQuantity} onChange={(e) => setForm({ ...form, purchasedQuantity: e.target.value })} style={s.input} />
              <input list="google-usage-units" placeholder="Unit" value={form.purchasedUnit} onChange={(e) => setForm({ ...form, purchasedUnit: e.target.value })} style={s.input} />
              <datalist id="google-usage-units">
                {unitOptions.map((unit) => <option key={unit} value={unit} />)}
              </datalist>
              <input type="number" step="0.0001" min="0" placeholder="Quoted rate" value={form.quotedUnitRate} onChange={(e) => setForm({ ...form, quotedUnitRate: e.target.value })} style={s.input} />
              <button style={s.saveBtn} disabled={saving}>{saving ? 'Saving...' : 'Save Mapping'}</button>
            </form>
          </section>
        )}

        {error && <div style={s.error}>{error}</div>}
        {message && <div style={s.message}>{message}</div>}

        <div style={s.summaryGrid}>
          <Stat label="Mapped SKUs" value={summary.mappedSkus || 0} />
          <Stat label="Purchased" value={fmt(summary.totalPurchased, 4)} />
          <Stat label="Utilized" value={fmt(summary.totalUtilized, 4)} />
          {/* <Stat label="Left Balance" value={fmt(summary.totalBalance, 4)} tone={summary.totalBalance < 0 ? 'danger' : 'normal'} /> */}
        </div>

        <section style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <h2 style={s.sectionTitle}>Usage Balance</h2>
              <p style={s.sectionSub}>
                {report?.customer?.name || selectedCustomer?.name || 'Customer'} · {startDate} to {endDate}
              </p>
            </div>
            <span style={s.badge}>{rows.length} rows</span>
          </div>

          {loading ? (
            <div style={s.empty}>Loading Google usage...</div>
          ) : !rows.length ? (
            <div style={s.empty}>No mapped SKU usage found for the selected customer and period.</div>
          ) : (
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {(isAdmin
                      ? ['Service', 'SKU Description', 'SKU ID', 'Purchased', 'Utilized', 'Left Balance', 'Unit', 'Quoted Rate', 'Cost']
                      : ['Service', 'SKU Description', 'SKU ID', 'Purchased', 'Utilized', 'Unit']
                    ).map((head) => (
                      <th key={head} style={s.th}>{head}</th>
                    ))}
                    {isAdmin && <th style={s.th}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedRows).map(([serviceName, serviceRows]) => (
                    <React.Fragment key={serviceName}>
                      <tr>
                        <td colSpan={isAdmin ? 10 : 7} style={s.groupCell}>{serviceName}</td>
                      </tr>
                      {serviceRows.map((row) => (
                        <tr key={row.mappingId}>
                          <td style={s.td}>{row.serviceName}</td>
                          <td style={{ ...s.td, maxWidth: 340 }}>{row.skuDescription}</td>
                          <td style={s.td}><code style={s.code}>{row.skuId}</code></td>
                          <td style={s.tdRight}>{fmt(row.purchasedQuantity, 4)}</td>
                          <td style={s.tdRight}>{fmt(row.utilizedQuantity, 4)}</td>
                          {/* <td style={{ ...s.tdRight, color: row.balanceQuantity < 0 ? '#dc2626' : '#166534', fontWeight: 700 }}>
                            {fmt(row.balanceQuantity, 4)}
                          </td> */}
                          <td style={s.td}>{row.unit || row.usageUnit || '-'}</td>
                          {isAdmin && <td style={s.tdRight}>{fmtOptional(row.quotedUnitRate, 4)}</td>}
                          {isAdmin && <td style={s.tdRight}>{row.currency ? `${row.currency} ${fmt(row.totalCost)}` : '-'}</td>}
                          {isAdmin && (
                            <td style={s.td}>
                              <button style={s.deleteBtn} onClick={() => removeMapping(row.mappingId)}>Remove</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const Stat = ({ label, value, tone = 'normal' }) => (
  <div style={s.stat}>
    <div style={s.statLabel}>{label}</div>
    <div style={{ ...s.statValue, color: tone === 'danger' ? '#dc2626' : '#0f172a' }}>{value}</div>
  </div>
);

const s = {
  layout: { display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
 main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  title: { margin: 0, color: '#0f172a', fontSize: 26, fontWeight: 700 },
  subtitle: { margin: '4px 0 0', color: '#64748b', fontSize: 13 },
  primaryBtn: { background: '#2563eb', border: 0, color: '#fff', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  filters: { display: 'grid', gridTemplateColumns: 'minmax(260px, 2fr) 180px 180px', gap: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldWide: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  input: { width: '100%', boxSizing: 'border-box', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', color: '#0f172a', fontSize: 13, outline: 'none' },
  section: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16, overflow: 'hidden' },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', borderBottom: '1px solid #f1f5f9' },
  sectionTitle: { margin: 0, color: '#0f172a', fontSize: 16, fontWeight: 700 },
  sectionSub: { margin: '4px 0 0', color: '#64748b', fontSize: 12 },
  badge: { background: '#f1f5f9', color: '#64748b', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700 },
  mappingForm: { display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1.4fr 140px 110px 120px 130px', gap: 10, padding: 16 },
  saveBtn: { background: '#0f766e', border: 0, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  deleteBtn: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 },
  message: { background: '#ecfdf5', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 16 },
  stat: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 },
  statLabel: { color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 },
  statValue: { color: '#0f172a', fontSize: 24, fontWeight: 800 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
  groupCell: { background: '#eef2ff', color: '#1e3a8a', fontSize: 13, fontWeight: 800, padding: '12px 14px', borderBottom: '1px solid #dbeafe' },
  td: { color: '#334155', fontSize: 13, padding: '13px 14px', borderBottom: '1px solid #f8fafc', verticalAlign: 'top' },
  tdRight: { color: '#334155', fontSize: 13, padding: '13px 14px', borderBottom: '1px solid #f8fafc', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  code: { background: '#f1f5f9', color: '#475569', borderRadius: 5, padding: '2px 6px', fontSize: 12 },
  empty: { padding: 42, textAlign: 'center', color: '#64748b', fontSize: 14 },
};
